import { useState, useEffect, useRef } from 'react';
import { testimonials as fallbackTestimonials } from '@/mocks/products';
import { supabase } from '@/lib/supabase';

const AVATAR_PLACEHOLDER = 'https://readdy.ai/api/search-image?query=neutral%20abstract%20avatar%20placeholder%20soft%20gradient%20circle%20minimal%20clean%20professional%20no%20face%20geometric%20shape%20warm%20tones%20orange%20pink&width=112&height=112&seq=avatar-placeholder-01&orientation=squarish';

interface Testimonial {
  id: string | number;
  name: string;
  role: string;
  text: string;
  rating: number;
  image: string;
}

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visible, setVisible] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>(fallbackTestimonials);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('id, user_name, rating, body, created_at')
          .gte('rating', 4)
          .order('created_at', { ascending: false })
          .limit(4);

        if (error || !data || data.length === 0) return;

        const mapped: Testimonial[] = data.map((r) => ({
          id: r.id,
          name: r.user_name || 'Anonymous',
          role: 'Verified Buyer',
          text: r.body || '',
          rating: r.rating,
          image: AVATAR_PLACEHOLDER,
        }));

        setTestimonials(mapped);
        setCurrentIndex(0);
      } catch {
        // silently fall back to mock data
      }
    };

    fetchReviews();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => nextTestimonial(), 5000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextTestimonial = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setIsAnimating(false);
    }, 350);
  };

  const prevTestimonial = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
      setIsAnimating(false);
    }, 350);
  };

  const goTo = (index: number) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 350);
  };

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Vibrant background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8, #fffbeb)' }} />
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f97316, transparent)', filter: 'blur(60px)', transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent)', filter: 'blur(60px)', transform: 'translate(-30%, 30%)' }} />

      {/* Big decorative quotes */}
      <div className="absolute top-8 left-8 text-[180px] font-black leading-none pointer-events-none select-none"
        style={{ color: 'rgba(249,115,22,0.06)' }}>"</div>
      <div className="absolute bottom-8 right-8 text-[180px] font-black leading-none pointer-events-none select-none rotate-180"
        style={{ color: 'rgba(236,72,153,0.06)' }}>"</div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-800 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold mb-5"
            style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)', boxShadow: '0 0 20px rgba(249,115,22,0.4)' }}>
            <i className="ri-chat-quote-line animate-heartbeat"></i>
            <span className="tracking-widest">TESTIMONIALS</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            What Our{' '}
            <span className="gradient-text-fire">Customers Say</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Don't just take our word for it — here's what our happy customers have to say
          </p>
        </div>

        {/* Main Testimonial */}
        <div className={`relative mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div
            className={`relative rounded-3xl p-8 md:p-12 transition-all duration-350 overflow-hidden ${
              isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,247,237,0.9))',
              border: '2px solid rgba(249,115,22,0.15)',
              boxShadow: '0 0 40px rgba(249,115,22,0.1), 0 20px 60px rgba(0,0,0,0.08)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {/* Decorative gradient corner */}
            <div className="absolute top-0 right-0 w-40 h-40 rounded-bl-full opacity-10"
              style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }} />
            <div className="absolute bottom-0 left-0 w-32 h-32 rounded-tr-full opacity-10"
              style={{ background: 'linear-gradient(135deg, #ec4899, #f59e0b)' }} />

            <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full animate-spin-slow"
                  style={{ background: 'conic-gradient(#f97316, #ec4899, #f59e0b, #f97316)', padding: '3px', borderRadius: '50%' }} />
                <div className="relative w-28 h-28 rounded-full overflow-hidden border-4 border-white"
                  style={{ boxShadow: '0 0 20px rgba(249,115,22,0.3)' }}>
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Verified badge */}
                <div className="absolute -bottom-1 -right-1 w-8 h-8 flex items-center justify-center rounded-full border-2 border-white"
                  style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}>
                  <i className="ri-check-line text-white text-xs font-bold"></i>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex gap-1 justify-center md:justify-start mb-4">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-xl" style={{ animationDelay: `${i * 0.1}s` }}></i>
                  ))}
                </div>
                <p className="text-xl md:text-2xl text-gray-700 font-medium mb-6 leading-relaxed italic">
                  &ldquo;{testimonials[currentIndex].text}&rdquo;
                </p>
                <div>
                  <h4 className="text-xl font-black text-gray-900">{testimonials[currentIndex].name}</h4>
                  <p className="font-semibold" style={{ color: '#f97316' }}>{testimonials[currentIndex].role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Nav buttons */}
          <button
            onClick={prevTestimonial}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-5 w-10 h-10 md:w-12 md:h-12 items-center justify-center rounded-full bg-white border-2 border-orange-100 text-gray-600 transition-all duration-300 hover:scale-110 cursor-pointer"
            style={{ boxShadow: '0 0 15px rgba(249,115,22,0.2)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #f97316, #ec4899)'; (e.currentTarget as HTMLButtonElement).style.color = 'white'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; (e.currentTarget as HTMLButtonElement).style.color = '#4b5563'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(249,115,22,0.2)'; }}
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={nextTestimonial}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-5 w-10 h-10 md:w-12 md:h-12 items-center justify-center rounded-full bg-white border-2 border-orange-100 text-gray-600 transition-all duration-300 hover:scale-110 cursor-pointer"
            style={{ boxShadow: '0 0 15px rgba(249,115,22,0.2)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #f97316, #ec4899)'; (e.currentTarget as HTMLButtonElement).style.color = 'white'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; (e.currentTarget as HTMLButtonElement).style.color = '#4b5563'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(249,115,22,0.2)'; }}
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mb-16">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className="transition-all duration-300 rounded-full cursor-pointer"
              style={{
                width: index === currentIndex ? '48px' : '12px',
                height: '12px',
                background: index === currentIndex
                  ? 'linear-gradient(90deg, #f97316, #ec4899)'
                  : 'rgba(0,0,0,0.15)',
                boxShadow: index === currentIndex ? '0 0 10px rgba(249,115,22,0.5)' : 'none',
              }}
            />
          ))}
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {testimonials.map((t, index) => (
            <div
              key={t.id}
              onClick={() => goTo(index)}
              className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-400 overflow-hidden ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
                background: index === currentIndex
                  ? 'linear-gradient(135deg, rgba(249,115,22,0.08), rgba(236,72,153,0.08))'
                  : 'rgba(255,255,255,0.8)',
                border: index === currentIndex
                  ? '2px solid rgba(249,115,22,0.4)'
                  : '2px solid rgba(0,0,0,0.06)',
                boxShadow: index === currentIndex
                  ? '0 0 20px rgba(249,115,22,0.15), 0 10px 30px rgba(0,0,0,0.08)'
                  : '0 4px 15px rgba(0,0,0,0.05)',
                transform: index === currentIndex ? 'translateY(-4px)' : 'translateY(0)',
              }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br from-orange-500 to-pink-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              <div className="flex items-center gap-3 mb-3">
                <div className="relative w-11 h-11 rounded-full overflow-hidden border-2"
                  style={{ borderColor: index === currentIndex ? '#f97316' : 'rgba(0,0,0,0.1)' }}>
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <h5 className="font-bold text-gray-900 text-sm">{t.name}</h5>
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-xs line-clamp-3 leading-relaxed">{t.text}</p>
              {index === currentIndex && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-2xl"
                  style={{ background: 'linear-gradient(90deg, #f97316, #ec4899)' }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
